import React, { useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    ClockClockwise, MagnifyingGlass, FunnelSimple, CaretLeft, CaretRight, ArrowsClockwise, FileText,
} from "@phosphor-icons/react";

const PAGE_SIZE = 25;

const ROLE_TONE = {
    admin: "bg-amber-100 text-amber-700",
    doctor: "bg-sky-100 text-sky-700",
    clinic_staff: "bg-violet-100 text-violet-700",
    staff: "bg-emerald-100 text-emerald-700",
    patient: "bg-rose-100 text-rose-700",
};

const ENTITY_TONE = {
    case: "bg-indigo-50 text-indigo-700 border-indigo-200",
    lead: "bg-amber-50 text-amber-700 border-amber-200",
    doctor: "bg-sky-50 text-sky-700 border-sky-200",
    patient: "bg-rose-50 text-rose-700 border-rose-200",
    staff: "bg-emerald-50 text-emerald-700 border-emerald-200",
    clinic_staff: "bg-violet-50 text-violet-700 border-violet-200",
    ticket: "bg-orange-50 text-orange-700 border-orange-200",
    application: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200",
};

function fmtDate(iso) {
    if (!iso) return ",";
    try {
        const d = new Date(iso);
        return d.toLocaleString("en-IN", {
            day: "2-digit", month: "short", year: "numeric",
            hour: "2-digit", minute: "2-digit",
        });
    } catch { return iso; }
}

export default function AdminAuditLog() {
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(true);
    const [facets, setFacets] = useState({ entity_types: [], actor_roles: [], actions: [] });
    const [filters, setFilters] = useState({
        entity_type: "", actor_role: "", action: "", q: "",
    });
    const [search, setSearch] = useState("");

    const load = useCallback(() => {
        setLoading(true);
        const params = { limit: PAGE_SIZE, offset };
        for (const [k, v] of Object.entries(filters)) {
            if (v) params[k] = v;
        }
        api.get("/admin/audit-logs", { params })
            .then((r) => {
                setItems(r.data.items || []);
                setTotal(r.data.total || 0);
            })
            .finally(() => setLoading(false));
    }, [offset, filters]);

    useEffect(() => {
        api.get("/admin/audit-logs/facets").then((r) => setFacets(r.data));
    }, []);
    useEffect(() => { load(); }, [load]);

    const onFilterChange = (key, value) => {
        setOffset(0);
        setFilters((f) => ({ ...f, [key]: value }));
    };

    const onSearch = (e) => {
        e.preventDefault();
        setOffset(0);
        setFilters((f) => ({ ...f, q: search.trim() }));
    };

    const clearFilters = () => {
        setOffset(0);
        setSearch("");
        setFilters({ entity_type: "", actor_role: "", action: "", q: "" });
    };

    const pageInfo = useMemo(() => {
        const from = total === 0 ? 0 : offset + 1;
        const to = Math.min(offset + items.length, total);
        return `${from},${to} of ${total}`;
    }, [items.length, offset, total]);

    const activeFilterCount = Object.values(filters).filter(Boolean).length;

    return (
        <div data-testid="admin-audit-page">
            <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl font-bold flex items-center gap-2.5">
                        <ClockClockwise size={28} weight="duotone" className="text-primary" />
                        Audit Log
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm">
                        Tamper-evident record of every state change on cases, leads, doctors, staff, tickets, and walk-in patients.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => load()} data-testid="audit-refresh-btn">
                    <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
                </Button>
            </div>

            {/* Filters */}
            <div className="bg-card border border-border rounded-2xl p-4 mb-5">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2 text-sm font-semibold">
                        <FunnelSimple size={16} className="text-muted-foreground" />
                        Filters
                        {activeFilterCount > 0 && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary text-white">{activeFilterCount}</span>
                        )}
                    </div>
                    {activeFilterCount > 0 && (
                        <button onClick={clearFilters} className="text-xs text-primary hover:underline" data-testid="audit-clear-filters">
                            Clear all
                        </button>
                    )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <FilterSelect
                        label="Entity"
                        value={filters.entity_type}
                        onChange={(v) => onFilterChange("entity_type", v)}
                        options={facets.entity_types}
                        testid="audit-filter-entity"
                    />
                    <FilterSelect
                        label="Actor role"
                        value={filters.actor_role}
                        onChange={(v) => onFilterChange("actor_role", v)}
                        options={facets.actor_roles}
                        testid="audit-filter-role"
                    />
                    <FilterSelect
                        label="Action"
                        value={filters.action}
                        onChange={(v) => onFilterChange("action", v)}
                        options={facets.actions}
                        testid="audit-filter-action"
                    />
                    <form onSubmit={onSearch}>
                        <label className="block text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">Search</label>
                        <div className="relative">
                            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Actor name or summary…"
                                className="h-9 pl-9 text-sm"
                                data-testid="audit-filter-q"
                            />
                        </div>
                    </form>
                </div>
            </div>

            {/* Results */}
            <div className="bg-card border border-border rounded-2xl overflow-hidden" data-testid="audit-list">
                {loading && (
                    <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
                )}
                {!loading && items.length === 0 && (
                    <div className="p-12 text-center">
                        <FileText size={32} className="mx-auto mb-2 opacity-40" />
                        <div className="text-sm text-muted-foreground">
                            {activeFilterCount > 0 ? "No events match these filters." : "No audit events yet."}
                        </div>
                    </div>
                )}
                {!loading && items.length > 0 && (
                    <ul className="divide-y divide-border">
                        {items.map((it) => (
                            <li key={it.id} className="p-4 hover:bg-secondary/40 transition-colors" data-testid={`audit-row-${it.id}`}>
                                <div className="flex items-start gap-3 flex-wrap">
                                    <div className={`w-8 h-8 rounded-lg border flex items-center justify-center text-[10px] font-bold uppercase shrink-0 ${ENTITY_TONE[it.entity_type] || "bg-secondary text-muted-foreground border-border"}`}>
                                        {(it.entity_type || "?").slice(0, 2)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="font-mono text-xs text-foreground/90 bg-secondary px-1.5 py-0.5 rounded">
                                                {it.action}
                                            </span>
                                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${ROLE_TONE[it.actor_role] || "bg-secondary text-muted-foreground"}`}>
                                                {it.actor_role}{it.actor_staff_type ? `/${it.actor_staff_type}` : ""}
                                            </span>
                                            <span className="text-xs text-muted-foreground">{fmtDate(it.created_at)}</span>
                                        </div>
                                        <div className="text-sm mt-1 truncate">
                                            <span className="font-semibold">{it.actor_name || ","}</span>
                                            <span className="text-muted-foreground"> · {it.summary || "(no summary)"}</span>
                                        </div>
                                        <div className="text-[11px] text-muted-foreground mt-0.5 font-mono truncate">
                                            {it.entity_type}:{(it.entity_id || "").slice(0, 8)}
                                            {it.meta && Object.keys(it.meta).length > 0 && (
                                                <span className="ml-2 opacity-70">{JSON.stringify(it.meta)}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                {/* Pagination */}
                {total > 0 && (
                    <div className="flex items-center justify-between p-3 border-t border-border bg-secondary/30">
                        <div className="text-xs text-muted-foreground">{pageInfo}</div>
                        <div className="flex items-center gap-2">
                            <Button
                                size="sm" variant="outline"
                                disabled={offset === 0}
                                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                                data-testid="audit-prev-page"
                            >
                                <CaretLeft size={14} /> Prev
                            </Button>
                            <Button
                                size="sm" variant="outline"
                                disabled={offset + items.length >= total}
                                onClick={() => setOffset(offset + PAGE_SIZE)}
                                data-testid="audit-next-page"
                            >
                                Next <CaretRight size={14} />
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function FilterSelect({ label, value, onChange, options, testid }) {
    return (
        <div>
            <label className="block text-[11px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">{label}</label>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full h-9 text-sm rounded-lg border border-border bg-background px-2"
                data-testid={testid}
            >
                <option value="">All</option>
                {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
            </select>
        </div>
    );
}
